// Attachment chips (D36): name, size and type of each file — in the composer (with remove and the upload state) and in
// the conversation history (user questions and the replies that read them). Never the content; an image chip shows a
// thumbnail (D36.4, AttachmentThumb.tsx: an <img> with a blob: URL, never markup).
import { useEffect, useState } from "react";
import type { AssistantAttachmentKind } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { IconButton } from "../../components/Button.tsx";
import { AlertTriangleIcon, FileTextIcon, LoaderIcon, PaperclipIcon, XIcon } from "../../components/icons.tsx";
import { formatBytes } from "../../lib/prdFile.ts";
import { useAttachmentPreview, type ContentFetcher } from "./AttachmentThumb.tsx";

export interface ChipItem {
  key: string;
  name: string;
  size: number;
  kind: AssistantAttachmentKind;
  state?: "uploading" | "ready" | "error";
  error?: string;
  /** D36.4 thumbnails: the image type, and the local file or the stored attachment id. */
  mime?: string;
  file?: Blob;
  attachmentId?: string;
}

function Chip({ item: a, onRemove, disabled, fetcher }: { item: ChipItem; onRemove?: ((key: string) => void) | undefined; disabled: boolean; fetcher?: ContentFetcher | undefined }) {
  const { t } = useI18n();
  const preview = useAttachmentPreview({ kind: a.kind, mime: a.mime, file: a.file, attachmentId: a.state === "error" ? undefined : a.attachmentId }, fetcher);
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [preview]);
  const thumb = preview && !broken && a.state !== "error" ? preview : null;
  const type = t(`composer.kind.${a.kind}`);
  const size = formatBytes(a.size);
  return (
    <li className={`attachment-chip${a.state === "error" ? " attachment-chip--error" : ""}${thumb ? " attachment-chip--thumb" : ""}`} data-state={a.state ?? "ready"} title={a.error ?? undefined}>
      {thumb ? (
        <img className="attachment-chip-thumb" src={thumb} alt="" aria-hidden="true" decoding="async" draggable={false} data-testid="attachment-thumb" onError={() => setBroken(true)} />
      ) : null}
      {a.state === "uploading" || a.state === "error" || !thumb ? (
        <span className="attachment-chip-icon" aria-hidden="true">
          {a.state === "uploading" ? <LoaderIcon size={14} className="assistant-spin" /> : a.state === "error" ? <AlertTriangleIcon size={14} /> : a.kind === "text" || a.kind === "document" ? <FileTextIcon size={14} /> : <PaperclipIcon size={14} />}
        </span>
      ) : null}
      <span className="sr-only">{t("composer.chipLabel", { name: a.name, size, type })}</span>
      <span className="attachment-chip-name truncate" aria-hidden="true">
        {a.name}
      </span>
      <span className="attachment-chip-meta subtle" aria-hidden="true">
        {size} · {type}
      </span>
      {a.state === "uploading" ? <span className="sr-only">{t("composer.uploading")}</span> : null}
      {a.state === "error" ? <span className="attachment-chip-error">{t("composer.uploadFailed", { reason: a.error ?? "" })}</span> : null}
      {onRemove ? (
        <IconButton label={t("composer.remove", { name: a.name })} onClick={() => onRemove(a.key)} disabled={disabled} className="attachment-chip-remove">
          <XIcon size={12} />
        </IconButton>
      ) : null}
    </li>
  );
}

export function AttachmentChips({
  items,
  onRemove,
  label,
  testId = "attachment-chips",
  disabled = false,
  fetcher,
}: {
  items: readonly ChipItem[];
  onRemove?: (key: string) => void;
  label?: string;
  testId?: string;
  disabled?: boolean;
  /** Test seam for the stored bytes of a thumbnail. */
  fetcher?: ContentFetcher;
}) {
  const { t } = useI18n();
  if (items.length === 0) return null;
  return (
    <ul className="attachment-chips" aria-label={label ?? t("composer.attachments")} data-testid={testId}>
      {items.map((a) => (
        <Chip key={a.key} item={a} onRemove={onRemove} disabled={disabled} fetcher={fetcher} />
      ))}
    </ul>
  );
}
